"use client";

import { useState, useContext } from "react";
import { useRouter } from "next/navigation";
import LoadingButton from "@mui/lab/LoadingButton";
import Alert from "@mui/material/Alert";
import { useForm, FormProvider } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";

import { TextField } from "components/form-hook";
import Label from "../components/label";
import EyeToggleButton from "../components/eye-toggle-button";
import usePasswordVisible from "../use-password-visible";
import { loginAction } from "utils/__api__/auth";
import { AuthContext } from "contexts/AuthContext";
import { Box, Button, Divider, Typography } from "@mui/material";

export default function LoginPageView() {
  const router = useRouter();
  const { login } = useContext(AuthContext);
  const { visiblePassword, togglePasswordVisible } = usePasswordVisible();
  const [blockedaccount, setBlockedaccount] = useState(false);
  const [error, setError] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  const schema = yup.object({
    email: yup.string().email("Correo no válido").required("El correo es requerido"),
    password: yup.string().required("La contraseña es obligatoria"),
  });

  const methods = useForm({
    resolver: yupResolver(schema),
    defaultValues: { email: "", password: "" },
  });

  const onSubmit = methods.handleSubmit(async (values) => {
    setError(null);
    setIsLoading(true);
    const authResponse = await loginAction(values);

    if (authResponse.success) {
      await login(authResponse.data);
      router.replace("/");
      return;
    }

    let message = authResponse.message;
    if (authResponse.status === 401) {
       message = authResponse.message;
       if (message === "La cuenta no está activa. Por favor contáctenos") {
         message = "La cuenta no está activa. Por favor contáctenos";
         setBlockedaccount(true);
       }
    }
    setError(message);
    setIsLoading(false);
    setTimeout(() => setError(null), 6000);
  });

  return (
    <FormProvider {...methods}>
      {!blockedaccount && (
        <form onSubmit={onSubmit} noValidate>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <div className="mb-1 mt-1">
            <Label>Correo electrónico</Label>
            <TextField
              fullWidth
              name="email"
              size="small"
              type="email"
              autoComplete="email"
              disabled={isLoading}
            />
          </div>

          <div className="mb-2">
            <Label>Contraseña</Label>
            <TextField
              fullWidth
              size="small"
              name="password"
              type={visiblePassword ? "text" : "password"}
              autoComplete="current-password"
              disabled={isLoading}
              InputProps={{
                endAdornment: (
                  <EyeToggleButton
                    show={visiblePassword}
                    click={togglePasswordVisible}
                  />
                ),
              }}
            />
          </div>

          <LoadingButton
            fullWidth
            size="large"
            type="submit"
            variant="contained"
            loading={isLoading}
            sx={{
              px: "2rem",
              color: "white",
              background: "rgb(23, 19, 85)",
              ":hover": { background: "rgb(23, 19, 85)" },
            }}
          >
            Iniciar sesión
          </LoadingButton>
        </form>
      )}

      {blockedaccount && (
        <Box mt={2} textAlign="center">
            <Typography>
              La cuenta no está activa. Nuestro equipo revisará la solicitud y recibirá un correo electrónico en cuanto sea aprobada.
            </Typography>
            <Divider sx={{ my: 2 }} />
            <Typography>
              Si necesitas ayuda, contacta a soporte:
            </Typography>
            <Button
              variant="outlined"
              color="primary"
              href="https://wa.me/522206784518?text=Hola,%20necesito%20ayuda%20con%20mi%20registro"
              target="_blank"
              sx={{ mt: 2 }}
            >
              Contactar soporte
            </Button>
        </Box>
      )}
    </FormProvider>
  );
}